import { useState, useCallback, useEffect, useRef } from 'react';
import { GlossaryBlueprint } from '@/types-games';
import { generateTempId, generateUUID } from '@/lib/utils/uuid';
import { getSelectedGame } from '@/lib/stores/unifiedUserStore';
import { GlossaryItem, ObjectItem, sectionDataObject, getCachedObjects } from "@/lib/stores/objectsStore";
import { parseBlueprintData } from '@/lib/utils/blueprintDataParser';
import { parseGlossaryItemData, createNewGlossaryItemFromBlueprint, rebuildGlossaryItemFromBlueprint } from "@/lib/utils/objectParser";
import { convertTagNamesToIds, convertTagIdsToNames } from '@/lib/utils';
import { applyBlueprintPropertiesToSections, addNewFieldsToSections, detectBlueprintChanges } from '@/lib/utils/blueprintVersionUtils';
import { validateFormFields, cleanupInvalidGlossaryReferences } from "./SectionFormFunctions/objectFormUtils";
import { getCachedTypes } from '@/lib/stores/typesStore';
import { getBlueprintVersionForSaving } from '@/lib/utils/versionUtils';
import { ObjectDraftData } from '@/lib/stores/wizardDraftStore';

interface UseGlossaryStateProps {
  initialData?: ObjectItem; // Glossary item data
  blueprint?: GlossaryBlueprint;
  draftData?: ObjectDraftData | null;
}

// Helper to create a signature of the data for comparison
function createDataSignature(data: any): string {
  if (!data) return '';
  try {
    return JSON.stringify(data);
  } catch {
    return '';
  }
}

export function useGlossaryState({ initialData, blueprint, draftData }: UseGlossaryStateProps) {
  // Track the last loaded object ID to detect when we switch between different objects
  const lastLoadedIdRef = useRef<string | undefined>(undefined);
  // Track the signature of the last initialized data to detect external changes
  const lastInitializedDataSignatureRef = useRef<string>('');
  // Track if we've initialized from draft (to only do it once)
  const hasInitializedFromDraftRef = useRef(false);

  // Helper to get initial value - prefer draft data for new items
  const getInitialValue = <T,>(draftValue: T | undefined, initialValue: T | undefined, defaultValue: T): T => {
    if (draftData && !initialData) {
      return draftValue !== undefined ? draftValue : defaultValue;
    }
    return initialValue !== undefined ? initialValue : defaultValue;
  };

  // Basic glossary item info
  const [itemTitle, setItemTitle] = useState(() => 
    getInitialValue(draftData?.name, initialData?.name, 'Untitled-1')
  );
  const [itemDescription, setItemDescription] = useState(initialData?.description || '');
  const [itemTags, setItemTags] = useState<string[]>(() =>
    getInitialValue(draftData?.tags, initialData?.tags, [])
  );
  const [itemIdentifiers, setItemIdentifiers] = useState<string[]>(() =>
    getInitialValue(draftData?.identifiers, initialData?.identifiers, [])
  );
  const [codeId, setCodeId] = useState<string>(initialData?.code_id || '');
  const [owner, setOwner] = useState<string | null>(() =>
    getInitialValue(draftData?.owner, initialData?.owner, null)
  );
  const [editPermissions, setEditPermissions] = useState<'Open' | 'Locked'>(() =>
    getInitialValue(draftData?.editPermissions, initialData?.edit_permissions, 'Open')
  );
  const [itemStatus, setItemStatus] = useState<string>(() =>
    getInitialValue(draftData?.status, initialData?.status, 'draft')
  );

  // Form data - the actual content of the glossary item
  const [formData, setFormData] = useState<Record<string, any>>({});
  // Blueprint reference
  const [selectedBlueprint, setSelectedBlueprint] = useState<GlossaryBlueprint | null>(blueprint || null);
  const [selectedBlueprintId, setSelectedBlueprintId] = useState<string>(blueprint?.id || '');

  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [isTagDropdownOpen, setIsTagDropdownOpen] = useState(false);
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);

  // Add new state for parsed form data
  const [parsedSections, setParsedSections] = useState<sectionDataObject[]>([]);
  
  // State for available tags (needed for conversion)
  const [availableTags, setAvailableTags] = useState<Array<{ id: string; name: string }>>([]);

  // Initialize form data from initialData, blueprint, or draftData
  // Track initialData.id separately to ensure effect runs when switching between objects
  // Also detect when external changes (like table edits) update initialData.data
  useEffect(() => {
    const currentObjectId = initialData?.id;
    const currentDataSignature = createDataSignature(initialData?.data);
    // Check if this is a different object (ID changed)
    const isNewObject = currentObjectId !== lastLoadedIdRef.current;
    // Check if external data has changed (same ID but different data signature)
    const hasExternalDataChange = 
      currentObjectId === lastLoadedIdRef.current && 
      currentDataSignature !== lastInitializedDataSignatureRef.current &&
      lastInitializedDataSignatureRef.current !== '';
    // Check if we need to restore draft for new items (both IDs are undefined but we have draft data)
    const shouldRestoreDraftForNewItem = 
      !initialData && 
      draftData && 
      draftData.sections && 
      draftData.sections.length > 0 && 
      !hasInitializedFromDraftRef.current;
    
    // Reinitialize if: new object OR external data change OR need to restore draft for new item
    if (isNewObject || hasExternalDataChange || shouldRestoreDraftForNewItem) {
      if (isNewObject) {
        console.log('[useGlossaryState] Loading new object:', currentObjectId);
      } else if (hasExternalDataChange) {
        console.log('[useGlossaryState] Detected external data change for object:', currentObjectId);
      }
      
      lastLoadedIdRef.current = currentObjectId;
      lastInitializedDataSignatureRef.current = currentDataSignature;
      
      if (initialData && blueprint) {
        // Editing existing glossary item with blueprint
        // Check if we have draft data to restore (user made changes before switching tabs)
        if (draftData && draftData.sections && draftData.sections.length > 0 && !hasInitializedFromDraftRef.current) {
          console.log('[useGlossaryState] Restoring draft data for existing item:', initialData.id);
          hasInitializedFromDraftRef.current = true;
          
          // Restore from draft
          setItemTitle(draftData.name || initialData.name || 'Untitled-1');
          setItemDescription(initialData.description || '');
          setItemTags(draftData.tags || initialData.tags || []);
          setItemIdentifiers(draftData.identifiers || initialData.identifiers || []);
          setOwner(draftData.owner ?? initialData.owner ?? null);
          setEditPermissions(draftData.editPermissions || initialData.edit_permissions || 'Open');
          setItemStatus(draftData.status || initialData.status || 'draft');
          
          setParsedSections(draftData.sections as sectionDataObject[]);
          
          // Convert sections to formData
          const newFormData: Record<string, any> = {};
          (draftData.sections as sectionDataObject[]).forEach((section: any) => {
            section.fields?.forEach((field: any) => {
              newFormData[field.id] = field.value;
            });
          });
          setFormData(newFormData);
          
          // Set blueprint
          setSelectedBlueprint(blueprint);
          setSelectedBlueprintId(blueprint.id);
        } else {
          // No draft, load from initialData normally
          setItemTitle(initialData.name || 'Untitled-1');
          setItemDescription(initialData.description || '');
          
          // If initialData contains tag IDs, convert them to tag names for display
          // This will be updated when availableTags is set
          setItemTags(initialData.tags || []);
          
          // Initialize identifiers from existing data
          setItemIdentifiers(initialData.identifiers || []);
          
          // Initialize owner and permissions
          setOwner(initialData.owner || null);
          setEditPermissions(initialData.edit_permissions || 'Open');
          
          // Initialize status
          setItemStatus(initialData.status || 'draft');
          
          setFormData(initialData.data || {});
          // Parse the existing data into sections
          const { sections } = parseGlossaryItemData(initialData, blueprint);
          setParsedSections(sections);
          // Set blueprint
          setSelectedBlueprint(blueprint);
          setSelectedBlueprintId(blueprint.id);
        }
      } else if (draftData && draftData.sections && draftData.sections.length > 0 && !hasInitializedFromDraftRef.current && !initialData) {
        // Initialize from draft data (for new items with saved draft)
        console.log('[useGlossaryState] Initializing from draft data:', draftData);
        hasInitializedFromDraftRef.current = true;
        
        setItemTitle(draftData.name || 'Untitled-1');
        setItemTags(draftData.tags || []);
        setItemIdentifiers(draftData.identifiers || []);
        setOwner(draftData.owner || null);
        setEditPermissions(draftData.editPermissions || 'Open');
        setItemStatus(draftData.status || 'draft');
        
        setParsedSections(draftData.sections as sectionDataObject[]);
        
        // Convert sections to formData
        const newFormData: Record<string, any> = {};
        (draftData.sections as sectionDataObject[]).forEach((section: any) => {
          section.fields?.forEach((field: any) => {
            newFormData[field.id] = field.value;
          });
        });
        setFormData(newFormData);
        
        // Set blueprint if available
        if (draftData.blueprintId) {
          setSelectedBlueprintId(draftData.blueprintId);
        }
      } else if (blueprint && !initialData && !hasInitializedFromDraftRef.current) {
        // Creating new glossary item from blueprint (no draft available)
        setSelectedBlueprint(blueprint);
        setSelectedBlueprintId(blueprint.id);

        // Initialize tags from blueprint when creating new item
        setItemTags(blueprint.tags || []);

        // Initialize form data from blueprint structure with proper ordering
        const { sections, formData: initialFormData } = createNewGlossaryItemFromBlueprint(blueprint);
        setParsedSections(sections);
        setFormData(initialFormData);
      }
    }
  }, [initialData?.id, blueprint?.id, initialData, blueprint, draftData]); // Track both IDs, objects, and draft

  // Convert tag IDs to tag names when availableTags is updated (for editing existing items)
  useEffect(() => {
    if (initialData && availableTags.length > 0 && initialData.tags) {
      // Convert tag IDs to tag names for display
      const tagNames = convertTagIdsToNames(initialData.tags, availableTags);
      setItemTags(tagNames);
    }
  }, [initialData, availableTags]);

  // Update blueprint selection
  const updateBlueprint = useCallback((blueprint: GlossaryBlueprint, initialFormData?: Record<string, any>) => {
    setSelectedBlueprint(blueprint);
    setSelectedBlueprintId(blueprint.id);

    // Update tags from new blueprint when switching, but preserve existing tags if editing
    if (initialData) {
      // If editing existing item, merge blueprint tags with existing tags (avoiding duplicates)
      const existingTags = itemTags;
      const blueprintTags = blueprint.tags || [];
      const mergedTags = [...new Set([...existingTags, ...blueprintTags])];
      setItemTags(mergedTags);
    } else {
      // If creating new item, use blueprint tags
      setItemTags(blueprint.tags || []);
    }

    // Parse new blueprint and reset form data with proper ordering
    const { sections, formData: blueprintFormData } = createNewGlossaryItemFromBlueprint(blueprint);
    console.log('Parsed blueprint sections with ordering:', sections);

    // Handle case where sections might be undefined
    if (!sections || sections.length === 0) {
      console.warn('No sections found in blueprint, using default structure');
      setParsedSections([]);
      setFormData({});
      return;
    }

    setParsedSections(sections);

    // Set form data with initial values if provided, otherwise use blueprint defaults
    if (initialFormData) {
      setFormData(initialFormData);
    } else {
      setFormData(blueprintFormData);
    }
  }, [initialData, itemTags]);

  // Update form field value
  const updateFormField = useCallback((fieldId: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: value
    }));

    // Also update the parsed sections for immediate UI updates
    setParsedSections(prev => prev.map(section => ({
      ...section,
      fields: section.fields.map(field =>
        field.id === fieldId ? { ...field, value } : field
      )
    })));
  }, []);

  // Update multiple form fields at once
  const updateFormFields = useCallback((updates: Record<string, any>) => {
    setFormData(prev => ({
      ...prev,
      ...updates
    }));

    // Update parsed sections as well
    setParsedSections(prev => prev.map(section => ({
      ...section,
      fields: section.fields.map(field => {
        if (updates[field.id] !== undefined) {
          return { ...field, value: updates[field.id] };
        }
        return field;
      })
    })));
  }, []);

  // Update entire sections (for reordering, adding, removing)
  const updateSections = useCallback((sections: sectionDataObject[]) => {
    setParsedSections(sections);

    // Convert sections back to form data format
    const newFormData: Record<string, any> = {};
    sections.forEach(section => {
      section.fields.forEach(field => {
        newFormData[field.id] = field.value;
      });
    });
    setFormData(newFormData);
  }, []);

  /**
   * Rebuild glossary structure from the current blueprint.
   * This ensures the structure always matches the latest blueprint when editing.
   * Existing field values are preserved where field IDs match.
   */
  const rebuildFromBlueprint = useCallback(() => {
    if (!selectedBlueprint || !initialData) {
      console.log('[GlossaryState] Cannot rebuild: missing blueprint or initialData');
      return;
    }

    console.log('[GlossaryState] Rebuilding glossary structure from blueprint...');
    
    // Rebuild the sections from the current blueprint, preserving existing values
    const { sections: rebuiltSections, formData: rebuiltFormData } = rebuildGlossaryItemFromBlueprint(
      selectedBlueprint,
      initialData
    );

    // Update the state with rebuilt structure
    setParsedSections(rebuiltSections);
    setFormData(rebuiltFormData);

    console.log('[GlossaryState] Rebuild complete:', {
      sectionsCount: rebuiltSections.length,
      fieldsCount: rebuiltSections.reduce((sum, s) => sum + s.fields.length, 0)
    });
  }, [selectedBlueprint, initialData]);

  // Get the complete glossary item data for saving
  const getGlossaryItemData = useCallback((sectionsOverride?: sectionDataObject[], updateBlueprintVersion?: boolean) => {
    if (!selectedBlueprint) {
      throw new Error('No blueprint selected');
    }

    // Use provided sections or fall back to state
    const sectionsToUse = sectionsOverride || parsedSections;

    // Check if we have at least a title (minimum requirement for any save)
    const hasTitle = itemTitle && itemTitle.trim() !== '';
    
    // Validate form fields, treating dropdown fields with no options as optional
    const currentGameId = getSelectedGame()?.id;
    const availableGlossaryItems = currentGameId ? getCachedObjects(currentGameId) : [];
    const availableTypes = currentGameId ? getCachedTypes(currentGameId) : [];
    
    const validation = validateFormFields(sectionsToUse, availableGlossaryItems, availableTypes);
    
    // Automatically determine status based on validation:
    // - If all required fields are filled (validation passes), automatically set to "published"
    // - If validation fails, set to "draft"
    // - This ensures the status reflects the actual completeness of the object
    const status = validation.isValid ? 'published' : 'draft';

    // Convert sectionsToUse to the expected format and clean up invalid references
    const formattedData: sectionDataObject[] = sectionsToUse.map(section => ({
      id: section.id,
      title: section.title,
      fields: section.fields.map(field => {
        let cleanedValue = field.value;
        
        // Clean up invalid glossary item references before saving
        if (field.type === 'glossaryItem' && field.value) {
          const currentGameId = getSelectedGame()?.id;
          if (currentGameId) {
            const availableItems = getCachedObjects(currentGameId);
            cleanedValue = cleanupInvalidGlossaryReferences(field.value, availableItems);
          }
        }
        
        return {
          id: field.id,
          type: field.type,
          label: field.label,
          value: cleanedValue,
          required: field.required,
          multiple: field.multiple,
          translation: field.translation,
          filter: field.filter,
          filterOptions: field.filterOptions,
          string_id: field.string_id, // Add this line to preserve the string_id
        };
      })
    }));
    
    // Convert tag names to tag IDs for storage
    const tagIds = convertTagNamesToIds(itemTags, availableTags);
    
    // Use title if available, otherwise generate a default for drafts
    const finalName = hasTitle ? itemTitle.trim() : `Untitled Draft ${new Date().toISOString().slice(0, 10)}`;
    
    // Determine blueprint version for saving:
    // - If updateBlueprintVersion is true (explicit migration), use current blueprint version
    // - If updateBlueprintVersion is false (normal save), preserve original blueprint_version
    // - If no initialData (new object), use current blueprint version
    const blueprintVersionForSaving = updateBlueprintVersion 
      ? getBlueprintVersionForSaving(selectedBlueprint)
      : (initialData?.blueprint_version !== undefined 
          ? initialData.blueprint_version 
          : getBlueprintVersionForSaving(selectedBlueprint));
    
    return {
      id: initialData?.id || generateTempId(),
      game_id: getSelectedGame()?.id,
      name: finalName,
      description: itemDescription, // Add description field
      type: "Library Object", // Add type field
      tags: tagIds, // Store tag IDs instead of tag names
      data: formattedData, // Use the converted data
      blueprint_id: selectedBlueprint.id,
      blueprint_version: blueprintVersionForSaving,
      identifiers: itemIdentifiers, // Add identifiers
      code_id: codeId, // Add code_id
      status: status, // Add draft/published status
      owner: owner, // Add owner
      edit_permissions: editPermissions, // Add edit permissions
      updated_at: new Date().toISOString(),
      created_at: initialData?.created_at || new Date().toISOString(),
    };
  }, [itemTitle, itemDescription, itemTags, itemIdentifiers, codeId, parsedSections, selectedBlueprint, initialData, availableTags, owner, editPermissions, itemStatus]);

  // Reset form to initial data or clear it
  const resetForm = useCallback(() => {
    if (initialData && blueprint) {
      // Reset to initial data
      setItemTitle(initialData.name || 'Untitled-1');
      setItemDescription(initialData.description || '');
      setItemTags(initialData.tags || []);
      setOwner(initialData.owner || null);
      setEditPermissions(initialData.edit_permissions || 'Open');
      setItemStatus(initialData.status || 'draft');
      setItemIdentifiers(initialData.identifiers || []);
      setCodeId(initialData.code_id || '');
      setFormData(initialData.data || {});
      
      // Parse the existing data into sections
      const { sections } = parseGlossaryItemData(initialData, blueprint);
      setParsedSections(sections);
      
      // Set blueprint
      setSelectedBlueprint(blueprint);
      setSelectedBlueprintId(blueprint.id);
    } else {
      // Clear form (for new items)
      setItemTitle('Untitled-1');
      setItemDescription('');
      setItemTags([]);
      setItemIdentifiers([]);
      setFormData({});
      setSelectedBlueprint(null);
      setSelectedBlueprintId('');
      setParsedSections([]);
    }
    setIsSaving(false);
  }, [initialData, blueprint]);

  // Set saving state
  const setSaving = useCallback((saving: boolean) => {
    setIsSaving(saving);
  }, []);

  // Function to update available tags (called from parent component)
  const updateAvailableTags = useCallback((tags: Array<{ id: string; name: string }>) => {
    setAvailableTags(tags);
  }, []);

  // Export current state as draft data (for persistence across tab switches)
  const getDraftData = useCallback((): ObjectDraftData => {
    return {
      name: itemTitle,
      tags: itemTags,
      identifiers: itemIdentifiers,
      sections: parsedSections,
      blueprintId: selectedBlueprintId,
      owner: owner,
      editPermissions: editPermissions,
      status: itemStatus,
    };
  }, [itemTitle, itemTags, itemIdentifiers, parsedSections, selectedBlueprintId, owner, editPermissions, itemStatus]);

  return {
    // State
    itemTitle,
    itemDescription,
    itemTags,
    itemIdentifiers,
    codeId,
    owner,
    editPermissions,
    itemStatus,
    formData,
    selectedBlueprint,
    selectedBlueprintId,
    isSaving,
    isTagDropdownOpen,
    isTypeDropdownOpen,
    parsedSections,
    availableTags,

    // Setters
    setItemTitle,
    setItemDescription,
    setItemTags,
    setItemIdentifiers,
    setCodeId,
    setOwner,
    setEditPermissions,
    setItemStatus,
    setFormData,
    setSelectedBlueprint,
    setSelectedBlueprintId,
    setIsSaving,
    setIsTagDropdownOpen,
    setIsTypeDropdownOpen,
    updateSections,

    // Actions
    updateBlueprint,
    updateFormField,
    updateFormFields,
    getGlossaryItemData,
    getDraftData,
    resetForm,
    setSaving,
    updateAvailableTags,
    rebuildFromBlueprint,
  };
}
